/**
 * The install gate.
 *
 * On a phone or tablet ABYSS is meant to be played as an installed app, not
 * inside a browser tab: fullscreen, landscape-locked, no address bar eating a
 * fifth of a 52-metre view height, and a service worker that has the whole game
 * on disk. In a normal mobile browser it shows a screen asking to be installed
 * instead of booting.
 *
 * ONE IMPORTANT CORRECTION to how this is usually specified. `navigator.share()`
 * does NOT offer "Add to Home Screen" on iOS. The Web Share API opens the system
 * sheet for sharing *content* — Messages, Mail, AirDrop, and whatever else has
 * registered as a share target. "Add to Home Screen" is an action in **Safari's
 * own** share menu, part of the browser's chrome, and a web page cannot open it.
 * Wiring a button to `navigator.share()` and telling the player to pick Add to
 * Home Screen produces a sheet that does not contain the thing the instructions
 * name, which is worse than showing no button at all. So iOS gets clear
 * illustrated instructions pointing at Safari's real Share control, and no
 * button that lies.
 *
 * Android is the opposite: `beforeinstallprompt` is a genuine hook and the
 * install really can be started from a button, so it is.
 *
 * Escape hatches, because a gate that cannot be opened is a bug waiting to
 * happen: `?play` skips it entirely, and it never applies to a desktop.
 */

type Platform = 'ios' | 'android' | 'other';

/** Running as an installed app rather than in a browser tab. */
export function isInstalled(): boolean {
  // `fullscreen` is here because the manifest lists it in `display_override`,
  // so an installed ABYSS on a browser that honours that reports fullscreen and
  // NOT standalone. It is also matched by a desktop browser sitting in F11
  // fullscreen, which makes this ambiguous in general — harmless here only
  // because `shouldGate` requires a handheld as well, and a handheld browser
  // has no F11. Do not reuse this predicate where a desktop answer matters.
  if (window.matchMedia('(display-mode: standalone)').matches
    || window.matchMedia('(display-mode: fullscreen)').matches
    || window.matchMedia('(display-mode: minimal-ui)').matches
    || (window.navigator as { standalone?: boolean }).standalone === true) {
    return true;
  }

  // THE LAUNCH MARKER, and the reason it exists.
  //
  // Not every "add to home screen" produces a real installed app. Several
  // Android browsers — Yandex among them — create a plain shortcut instead,
  // and a shortcut opens a normal browser tab no matter what `display` the
  // manifest asks for. The player taps the icon they just made, the browser
  // comes back, and none of the display-mode queries above are true, so the
  // game shows them the install screen they have already followed. That is the
  // exact loop reported.
  //
  // `start_url` carries `?app=1`, so anything launched from that icon says so
  // in its URL whether or not the browser gave it its own window. Remembered
  // for the session, because in-app navigation drops the query string, and in
  // sessionStorage rather than localStorage so it cannot leak into an ordinary
  // browser tab opened later.
  try {
    if (new URLSearchParams(location.search).has('app')) {
      sessionStorage.setItem(LAUNCH_KEY, '1');
      return true;
    }
    return sessionStorage.getItem(LAUNCH_KEY) === '1';
  } catch {
    // Private mode can throw on access. A gate we cannot remember past is
    // better than a crash.
    return new URLSearchParams(location.search).has('app');
  }
}

const LAUNCH_KEY = 'abyss.launched';

/**
 * Fullscreen and a landscape lock, on the first gesture.
 *
 * `display: "fullscreen"` in the manifest was doing this job alone, and it is
 * the least honoured display value there is — a browser that does not implement
 * it is supposed to walk the chain down to standalone, and several instead drop
 * straight to a browser tab. The manifest asks for `standalone` now, which
 * virtually everything honours, and the fullscreen that value does not buy is
 * taken here instead.
 *
 * Both calls need a user gesture and both reject freely — on iOS there is no
 * element fullscreen at all, and orientation locking is unsupported outside an
 * installed app. Every failure is fine and none of them are worth reporting.
 */
export function goFullscreen() {
  const el = document.documentElement as HTMLElement & {
    webkitRequestFullscreen?: () => Promise<void>;
  };
  try {
    if (!document.fullscreenElement) {
      void (el.requestFullscreen?.({ navigationUI: 'hide' }) ?? el.webkitRequestFullscreen?.())
        ?.catch(() => {});
    }
  } catch { /* not permitted here */ }
  try {
    const o = screen.orientation as ScreenOrientation & { lock?: (m: string) => Promise<void> };
    void o?.lock?.('landscape').catch(() => {});
  } catch { /* not permitted here */ }
}

export function isHandheld(): boolean {
  const coarse = window.matchMedia('(pointer: coarse)').matches;
  if (!coarse) return false;
  if (!window.matchMedia('(any-pointer: fine)').matches) return true;

  // A STYLUS REPORTS A FINE POINTER, and `any-pointer: fine` alone was enough
  // to disqualify a device. A Galaxy with an S Pen therefore answered "not a
  // handheld", skipped the gate entirely, and booted the full engine inside a
  // browser tab — which is how a phone ended up on the WebGPU error screen
  // instead of on the install screen it was supposed to see.
  //
  // A phone with a pen is still a phone. Requiring a small screen alongside the
  // coarse pointer keeps a touchscreen laptop out (large) while letting a
  // stylus phone in, and touch points confirm there is a finger involved at all.
  const shortEdge = Math.min(window.innerWidth, window.innerHeight);
  return shortEdge <= 820 && navigator.maxTouchPoints > 0;
}

/**
 * An Android browser that is not Chrome, which on this platform is the whole
 * difference between "your device cannot run this" and "this browser cannot".
 */
export function androidNonChrome(): boolean {
  const ua = navigator.userAgent;
  if (!/Android/.test(ua)) return false;
  // Every Chromium fork carries `Chrome/` in its UA, so testing for that alone
  // said Yandex WAS Chrome and the advice never appeared on the one browser it
  // was reported from. The vendor's own token is the only reliable tell, and
  // the list will always be incomplete — hence `androidGpuAdvice` below, which
  // does not depend on recognising anybody.
  return /YaBrowser|Yowser|SamsungBrowser|FxiOS|Firefox|OPR\/|EdgA\/|UCBrowser|MiuiBrowser|HuaweiBrowser|HeyTapBrowser|VivoBrowser|Quark|QQBrowser|Whale/
    .test(ua)
    || !/Chrome\//.test(ua);
}

/** Reopen the current page in Chrome. Android intent URL; needs a gesture. */
export function chromeIntentUrl(): string {
  const url = location.href.replace(/^https?:\/\//, '');
  return `intent://${url}#Intent;scheme=https;package=com.android.chrome;end`;
}

/**
 * iOS major version, or 0 when it cannot be read.
 *
 * Only used to decide whether the photographed instructions match the Safari
 * the player is actually looking at. Apple moves this chrome between releases —
 * the share control used to live in the toolbar and now hides behind ••• — so a
 * screenshot is only ever true for the versions it was taken on. Gating on the
 * version is what lets us show a real photograph without it silently rotting
 * into a lie two releases from now.
 *
 * `Version/26.0` is the Safari token and is present on iPhone and on
 * desktop-class iPad alike; `CPU iPhone OS 26_0` is the fallback.
 */
function iosMajor(): number {
  const ua = navigator.userAgent;
  const v = /Version\/(\d+)/.exec(ua) ?? /(?:iPhone )?OS (\d+)[_.]/.exec(ua);
  return v ? parseInt(v[1], 10) : 0;
}

/** Versions the screenshots in `public/install` were actually taken on. */
const SHOT_VERSIONS = [26, 27];

function platform(): Platform {
  const ua = navigator.userAgent;
  // iPadOS 13+ reports itself as a Mac; the touch-point count is what gives it
  // away, and it is the only reliable tell that does not need UA-CH.
  const iOS = /iPad|iPhone|iPod/.test(ua)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (iOS) return 'ios';
  if (/Android/.test(ua)) return 'android';
  return 'other';
}

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: InstallPromptEvent | null = null;

/**
 * Start listening immediately — Chrome fires this early and once, so it has to
 * be captured before anything async happens or the button has nothing to call.
 */
export function watchForInstallPrompt() {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferred = e as InstallPromptEvent;
  });
}

/**
 * Should the game boot? Returns false when the gate is showing, in which case
 * nothing expensive — no GPU, no shaders, no terrain — should be started.
 */
export function shouldGate(): boolean {
  if (new URLSearchParams(location.search).has('play')) return false;
  return isHandheld() && !isInstalled();
}

/** Render the gate. It replaces the boot card rather than sitting over it. */
export function showInstallGate() {
  const plat = platform();
  const host = document.getElementById('boot');
  if (!host) return;

  host.classList.remove('hidden');
  host.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'gate';

  const title = document.createElement('h1');
  title.textContent = 'ABYSS';

  const lede = document.createElement('p');
  lede.className = 'gate-lede';
  lede.textContent = 'THE OCEAN IS WAITING';

  wrap.append(title, lede);

  if (plat === 'android') {
    const btn = document.createElement('button');
    btn.className = 'gate-btn';
    btn.textContent = 'INSTALL & DIVE';
    const note = document.createElement('p');
    note.className = 'gate-note';
    note.textContent = 'INSTALL TO PLAY FULLSCREEN';

    btn.addEventListener('click', async () => {
      if (!deferred) {
        // Chrome withholds the event when the app is already installed, when
        // the manifest fails validation, or on a second visit within its
        // heuristics. Say what to do instead of failing silently.
        note.textContent = 'OPEN THE BROWSER MENU AND CHOOSE “INSTALL APP”';
        return;
      }
      btn.disabled = true;
      await deferred.prompt();
      const { outcome } = await deferred.userChoice;
      deferred = null;
      btn.disabled = false;
      note.textContent = outcome === 'accepted'
        ? 'INSTALLED — LAUNCH ABYSS FROM YOUR HOME SCREEN'
        : 'INSTALL TO PLAY FULLSCREEN';
    });
    wrap.append(btn, note);
  } else if (plat === 'ios') {
    // No button. See the note at the top of this file: there is no API that can
    // open Safari's Add to Home Screen, and a button that opens the wrong sheet
    // is worse than instructions that are simply true.
    // The steps name what is actually ON SCREEN.
    //
    // "TAP THE SHARE BUTTON IN SAFARI" was wrong for the Safari most people now
    // have: with the bottom address bar there is no share button in the bar at
    // all. The real route is the ••• at the bottom right, then Share, and only
    // then Add to Home Screen — three taps, two of them invisible if you are
    // told to look for a button that is not there.
    //
    // On the versions those photographs were taken on, the photographs ARE the
    // instructions: the player is looking for a specific control in specific
    // chrome, and a picture of it beats any sentence. On anything else we fall
    // back to drawn glyphs, because a screenshot of the wrong Safari is worse
    // than a diagram of the right idea.
    const steps = document.createElement('ol');
    steps.className = 'gate-steps';
    const shots = SHOT_VERSIONS.includes(iosMajor());

    if (shots) {
      steps.classList.add('gate-shots');
      for (const [src, text] of [
        ['/install/ios26-step1.webp', 'TAP ••• AT THE BOTTOM RIGHT'],
        ['/install/ios26-step2.webp', 'CHOOSE “SHARE”'],
        ['/install/ios26-step3.webp', 'SCROLL AND TAP “ADD TO HOME SCREEN”'],
      ] as const) {
        const li = document.createElement('li');
        const cap = document.createElement('span');
        cap.textContent = text;
        const img = document.createElement('img');
        img.src = src;
        img.alt = '';
        img.decoding = 'async';
        li.append(cap, img);
        steps.append(li);
      }
      const last = document.createElement('li');
      const lastCap = document.createElement('span');
      lastCap.textContent = 'OPEN ABYSS FROM YOUR HOME SCREEN';
      last.append(lastCap);
      steps.append(last);
    } else {
      const ICON = {
        dots: '<svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">'
          + '<circle cx="5" cy="12" r="2" fill="currentColor"/>'
          + '<circle cx="12" cy="12" r="2" fill="currentColor"/>'
          + '<circle cx="19" cy="12" r="2" fill="currentColor"/></svg>',
        share: '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" '
          + 'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" '
          + 'stroke-linejoin="round" aria-hidden="true">'
          + '<path d="M12 15V3"/><path d="M8.5 6.5 12 3l3.5 3.5"/>'
          + '<path d="M6 12H4.8A1.8 1.8 0 0 0 3 13.8v5.4A1.8 1.8 0 0 0 4.8 21h14.4a1.8 '
          + '1.8 0 0 0 1.8-1.8v-5.4A1.8 1.8 0 0 0 19.2 12H18"/></svg>',
        home: '<svg viewBox="0 0 24 24" width="19" height="19" fill="none" '
          + 'stroke="currentColor" stroke-width="1.7" stroke-linecap="round" '
          + 'aria-hidden="true">'
          + '<rect x="3.5" y="3.5" width="17" height="17" rx="4.5"/>'
          + '<path d="M12 8.5v7M8.5 12h7"/></svg>',
        play: '<svg viewBox="0 0 24 24" width="19" height="19" aria-hidden="true">'
          + '<path d="M8 5.5v13l11-6.5z" fill="currentColor"/></svg>',
      };
      for (const [icon, text] of [
        [ICON.share, 'TAP THE SHARE ARROW IN SAFARI'],
        [ICON.home, 'SCROLL AND TAP “ADD TO HOME SCREEN”'],
        [ICON.play, 'OPEN ABYSS FROM YOUR HOME SCREEN'],
      ] as const) {
        const li = document.createElement('li');
        const g = document.createElement('span');
        g.className = 'gate-glyph';
        g.innerHTML = icon;
        const t = document.createElement('span');
        t.textContent = text;
        li.append(g, t);
        steps.append(li);
      }
      void ICON.dots;
    }

    const alt = document.createElement('p');
    alt.className = 'gate-note';
    alt.textContent = shots
      ? 'THE PICTURES ARE YOUR SAFARI'
      : 'NEWER SAFARI HIDES SHARE BEHIND ••• AT THE BOTTOM RIGHT';

    const note = document.createElement('p');
    note.className = 'gate-note';
    note.textContent = 'INSTALL TO PLAY FULLSCREEN';
    wrap.append(steps, alt, note);
  } else {
    const note = document.createElement('p');
    note.className = 'gate-note';
    note.textContent = 'ADD ABYSS TO YOUR HOME SCREEN TO PLAY';
    wrap.append(note);
  }

  host.append(wrap);
}
